import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getDopplerFrameIndexes,
  isVisibleDopplerLayerLoading,
} from '../utils/dopplerLayer.utils.js';

const isFrameIndexMissing = (frameIndex) => frameIndex === null || frameIndex === undefined;

// `hasBeenPrimed` is true once every frame has loaded. Playback does not wait for it:
// `hasStarted` turns true as soon as the displayed frame is usable, and the animation then waits on
// each frame that is still loading. It stays true until the frame collection resets, so a pan before
// priming does not disable the controls.
export default function useDopplerBufferState({ frameCount, displayedFrameIndex }) {
  const [usableFrames, setUsableFrames] = useState({});
  const [hasBeenPrimed, setHasBeenPrimed] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isVisibleLayerLoading, setIsVisibleLayerLoading] = useState(false);
  const layerLoadingStateRef = useRef({});
  const layerErrorStateRef = useRef({});
  const hasBeenPrimedRef = useRef(false);

  const startupFrameIndexes = useMemo(() => getDopplerFrameIndexes(frameCount), [frameCount]);
  const startupReadyFrameCount = useMemo(
    () => startupFrameIndexes.filter((frameIndex) => usableFrames[frameIndex]).length,
    [startupFrameIndexes, usableFrames],
  );

  const markFrameUsable = useCallback((frameIndex) => {
    if (isFrameIndexMissing(frameIndex)) return;

    setUsableFrames((currentFrames) => {
      if (currentFrames[frameIndex]) return currentFrames;

      return { ...currentFrames, [frameIndex]: true };
    });
  }, []);

  const markFramePending = useCallback((frameIndex) => {
    if (isFrameIndexMissing(frameIndex)) return;

    setUsableFrames((currentFrames) => {
      if (!currentFrames[frameIndex]) return currentFrames;

      const nextFrames = { ...currentFrames };
      delete nextFrames[frameIndex];
      return nextFrames;
    });
  }, []);

  const clearLayerTracking = useCallback(() => {
    layerLoadingStateRef.current = {};
    layerErrorStateRef.current = {};
  }, []);

  const resetBufferState = useCallback(() => {
    clearLayerTracking();
    hasBeenPrimedRef.current = false;
    setUsableFrames({});
    setHasBeenPrimed(false);
    setHasStarted(false);
    setIsVisibleLayerLoading(false);
  }, [clearLayerTracking]);

  const prepareFrame = useCallback((frameIndex) => {
    layerErrorStateRef.current[frameIndex] = false;
  }, []);

  const markLayerLoading = useCallback(
    (frameIndex) => {
      layerLoadingStateRef.current[frameIndex] = true;
      layerErrorStateRef.current[frameIndex] = false;

      if (!hasBeenPrimedRef.current) {
        markFramePending(frameIndex);
      }
    },
    [markFramePending],
  );

  const markLayerLoaded = useCallback(
    (frameIndex) => {
      layerLoadingStateRef.current[frameIndex] = false;

      if (layerErrorStateRef.current[frameIndex]) {
        return false;
      }

      markFrameUsable(frameIndex);
      return true;
    },
    [markFrameUsable],
  );

  const markLayerError = useCallback(
    (frameIndex) => {
      layerErrorStateRef.current[frameIndex] = true;

      if (!hasBeenPrimedRef.current) {
        markFramePending(frameIndex);
      }
    },
    [markFramePending],
  );

  const clearLayerError = useCallback((frameIndex) => {
    layerErrorStateRef.current[frameIndex] = false;
  }, []);

  const markLayerNotLoading = useCallback((frameIndex) => {
    layerLoadingStateRef.current[frameIndex] = false;
  }, []);

  const isFrameLoading = useCallback(
    (frameIndex) => Boolean(layerLoadingStateRef.current[frameIndex]),
    [],
  );

  const syncUsableFramesFromLayers = useCallback(
    (layerEntries) => {
      layerEntries.forEach(({ frameIndex, layer }) => {
        if (!layer?.isLoading?.() && !layerErrorStateRef.current[frameIndex]) {
          markFrameUsable(frameIndex);
        }
      });
    },
    [markFrameUsable],
  );

  const syncVisibleLayerLoadingState = useCallback((currentFrame, trailFrame) => {
    setIsVisibleLayerLoading(
      isVisibleDopplerLayerLoading(layerLoadingStateRef.current, currentFrame, trailFrame),
    );
  }, []);

  useEffect(() => {
    if (
      hasBeenPrimed ||
      !startupFrameIndexes.length ||
      !startupFrameIndexes.every((frameIndex) => usableFrames[frameIndex])
    ) {
      return;
    }

    setHasBeenPrimed(true);
  }, [hasBeenPrimed, startupFrameIndexes, usableFrames]);

  useEffect(() => {
    hasBeenPrimedRef.current = hasBeenPrimed;
  }, [hasBeenPrimed]);

  useEffect(() => {
    if (
      hasStarted ||
      isFrameIndexMissing(displayedFrameIndex) ||
      !usableFrames[displayedFrameIndex]
    ) {
      return;
    }

    setHasStarted(true);
  }, [displayedFrameIndex, hasStarted, usableFrames]);

  return {
    clearLayerError,
    clearLayerTracking,
    hasBeenPrimed,
    isFrameLoading,
    isPlaybackReady: hasStarted || hasBeenPrimed,
    isVisibleLayerLoading,
    markLayerError,
    markLayerLoaded,
    markLayerLoading,
    markLayerNotLoading,
    prepareFrame,
    resetBufferState,
    showLoadingOverlay: !hasBeenPrimed,
    startupReadyFrameCount,
    startupTargetFrameCount: startupFrameIndexes.length,
    syncUsableFramesFromLayers,
    syncVisibleLayerLoadingState,
    usableFrames,
  };
}
